import { faClose, faMessage } from "@fortawesome/free-solid-svg-icons"
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import { CanView } from "../../../components/CanView/CanView"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import InputField from "../../../components/InputField/InputField"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { Permissions } from "../../../Enum/PermissionsType"
import { companyMemory } from "../../../Objects/Memory/Memory"
import { useCreateCompany } from "../../../hooks/Company/useCreateCompany"
import { useGetCompany } from "../../../hooks/Company/useGetCompany"
import { useSaveCompany } from "../../../hooks/Company/useSaveCompany"
import { usePermission } from "../../../hooks/Permissions/usePermission"
import { INPUT_SIZE } from "../../../configuration/InputSize"
import { SIZE } from "../../../configuration/Sizes"
import THEME from "../../../configuration/Theme"
import { useError } from "../../../context/useErrorContext"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import GetSpace from "../../../utils/space"

interface WhatsappOrderSupportPopupProps {
  onClose: () => void
}

export const WhatsappOrderSupportPopup: React.FC<WhatsappOrderSupportPopupProps> = ({ onClose }) => {
  const { t } = useTranslation("company")

  const { setError } = useError()

  const { handleSaveCompany } = useSaveCompany()

  const { handleGetCompany } = useGetCompany()

  const canEdite = !usePermission(Permissions.CanEditeCompany)

  const {
    whatsappPhone,
    whatsappOrderSupportMessageTemplate,
    handleWhatsappPhoneChange,
    handleWhatsappOrderSupportMessageTemplateChange,
    handleInitialValue,
    handleSubmitForm,
  } = useCreateCompany()

  useEffect(() => {
    handleInitialValue(companyMemory)
  }, [])

  const handleTrySave = () => {
    const normalizedPhone = whatsappPhone.replace(/\D/g, "")
    const hasValidWhatsappPhone = normalizedPhone.length >= 12 && normalizedPhone.length <= 13

    if (whatsappPhone.trim().length > 0 && !hasValidWhatsappPhone) {
      setError(t("general:label_invalid"), t("label_whatsapp_phone_invalid"))
      return
    }

    const data = handleSubmitForm()

    handleSaveCompany(data, () => {
      handleGetCompany(() => handleInitialValue(companyMemory))
      onClose()
    })
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert1}>
            <TitlePageResponsiveIconSmall
              title={t("label_title_whatsapp_order_support")}
              iconColor={THEME.colors.iconBGColor}
              icon={faMessage}
              children={
                <div className="flex items-center gap-1">
                  <PopupHelpButton
                    helpKey="pages.mystore.mycompany.whatsappordersupportpopup.1"
                    variant="global"
                    title={t("helpPopup:global.whatsappOrderSupport.title")}
                    description={t("helpPopup:global.whatsappOrderSupport.description")}
                    bullets={t("helpPopup:global.whatsappOrderSupport.bullets", { returnObjects: true }) as string[]}
                  />

                  <ButtonIcon
                    sizeNumber={SIZE.buttonCloseIconSize}
                    bgColor={THEME.colors.buttonBGCloseColor}
                    iconColor={THEME.colors.buttonFGCloseColor}
                    icon={faClose}
                    onClick={onClose}
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.whatsappOrderSupportPopup.button.onClose"
                  />
                </div>
              }
            />

            <GetSpace top={10} />

            <form
              className="p-2"
              onSubmit={(e) => {
                e.preventDefault()
                e.stopPropagation()
                handleTrySave()
              }}
            >
              <InputField
                maxLength={INPUT_SIZE.COMPANY.whatsappPhone}
                type="text"
                placeholder={t("label_whatsapp_phone")}
                icon={faWhatsapp}
                disable={canEdite}
                value={whatsappPhone}
                onChange={handleWhatsappPhoneChange}
                descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.whatsappOrderSupportPopup.input.whatsappPhone"
              />

              <GetSpace top={20} />

              <Animate type={AnimationTypes.fadeInTraslate}>
                <p className="mb-2 text-xs p-1 font-bold uppercase tracking-wider text-orange-600">
                  {t("label_whatsapp_order_support_template_title")}
                </p>
              </Animate>

              <Animate type={AnimationTypes.fadeInTraslate}>
                <div
                  className={`rounded-xl border bg-white ring-2 ring-gray-200 border-transparent p-3 transition-all duration-200 ${
                    canEdite
                      ? "bg-gray-100 border-gray-200 cursor-not-allowed"
                      : "border-gray-300 focus-within:ring-2 focus-within:ring-orange-500 focus-within:border-orange-400"
                  }`}
                >
                  <textarea
                    maxLength={INPUT_SIZE.COMPANY.whatsappOrderTemplate}
                    value={whatsappOrderSupportMessageTemplate}
                    onChange={handleWhatsappOrderSupportMessageTemplateChange}
                    disabled={canEdite}
                    placeholder={t("label_whatsapp_order_support_template")}
                    className="min-h-32 w-full resize-y bg-transparent p-2 text-sm text-gray-800 placeholder-gray-400 outline-none disabled:text-gray-400"
                    data-interaction-description-key="interactionDescriptions:pages.myRestaurant.myCompany.whatsappOrderSupportPopup.input.whatsappOrderSupportTemplate"
                  />
                </div>
              </Animate>

              <Animate type={AnimationTypes.fadeInTraslate}>
                <p className="mt-2 text-xs p-1 text-slate-500">{t("label_whatsapp_order_support_template_hint")}</p>
              </Animate>

              <div className={defaultStyle.divisionBar}></div>

              <section className={defaultStyle.buttonsController}>
                <CanView permission={Permissions.CanEditeCompany}>
                  <Button
                    sizeNumber={SIZE.buttonCloseIconSize}
                    label={t("buttons:button_save")}
                    typeButton="submit"
                    type="primary"
                    size="medium"
                    bgColor={THEME.colors.buttonBGSaveColor}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.whatsappOrderSupportPopup.button.save"
                  />
                </CanView>
              </section>
            </form>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
